import React, { useMemo } from 'react';
import clsx from 'clsx';
import { EmailTemplate } from '@app-types';
import styles from './TemplatesSidebar.module.css';

export interface TemplatesSidebarProps {
  templates: EmailTemplate[];
  selectedCode: string | null;
  hasChanges: boolean;
  onSelectTemplate: (code: string) => void;
}

interface TemplateGroup {
  name: string;
  order: number;
  templates: EmailTemplate[];
}

/** Fallback group name for templates with a null/empty display_group. Sorts last. */
const FALLBACK_GROUP = 'Other';

/** Space-free, stable slug for use in a data-test-id (e.g. "Pending Evidence" -> "pending-evidence"). */
const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-)|(-$)/g, '');

/**
 * Group templates by `display_group` and order them by `display_order`:
 * items within a group are sorted by display_order, and groups are ordered by
 * the minimum display_order of their items. Templates with a null/empty
 * display_group fall into an "Other" group that always sorts last.
 */
const groupTemplates = (templates: EmailTemplate[]): TemplateGroup[] => {
  const groups = new Map<string, TemplateGroup>();

  for (const template of templates) {
    const groupName = template.display_group || FALLBACK_GROUP;
    const existing = groups.get(groupName);
    if (existing) {
      existing.templates.push(template);
      existing.order = Math.min(existing.order, template.display_order);
    } else {
      groups.set(groupName, {
        name: groupName,
        order: template.display_order,
        templates: [template]
      });
    }
  }

  const ordered = Array.from(groups.values());
  // The fallback "Other" group always sorts after real groups, regardless of
  // its items' display_order, so a null group never floats to the top.
  ordered.sort((a, b) => {
    if (a.name === FALLBACK_GROUP) return b.name === FALLBACK_GROUP ? 0 : 1;
    if (b.name === FALLBACK_GROUP) return -1;
    return a.order - b.order;
  });
  ordered.forEach((group) => {
    group.templates = [...group.templates].sort((a, b) => a.display_order - b.display_order);
  });
  return ordered;
};

const TemplatesSidebar: React.FC<TemplatesSidebarProps> = ({
  templates,
  selectedCode,
  hasChanges,
  onSelectTemplate
}) => {
  const groups = useMemo(() => groupTemplates(templates), [templates]);

  return (
    <aside className={styles.templatesSidebar} data-test-id="templates-sidebar">
      <h2>Templates</h2>
      {groups.map((group) => (
        <div key={group.name} className={styles.templateGroup} data-test-id={`template-group-${slugify(group.name)}`}>
          <h3 className={styles.groupHeader} data-test-id="template-group-header">
            {group.name}
          </h3>
          <ul className={styles.templateList}>
            {group.templates.map((template: EmailTemplate) => (
              <li
                key={template.template_code}
                className={clsx(
                  styles.templateItem,
                  selectedCode === template.template_code && styles.templateItemActive
                )}
                data-test-id={`template-item-${template.template_code}`}
              >
                <button
                  type="button"
                  className={styles.templateButton}
                  onClick={() => onSelectTemplate(template.template_code)}
                >
                  <span className={styles.templateName}>{template.name}</span>
                  {template.template_code === selectedCode && hasChanges && (
                    <span className={styles.unsavedIndicator} title="Unsaved changes">
                      *
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </aside>
  );
};

export default TemplatesSidebar;
